import { supabase } from '@/lib/supabase'
import type { Ticket, TicketInsert, TicketUpdate, Message, MessageInsert } from '@/types/database'

export interface TicketWithDetails extends Ticket {
  user?: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  }
  project?: {
    id: string
    title: string
  }
  assigned_admin?: {
    id: string
    full_name: string | null
    email: string
    avatar_url: string | null
  }
  message_count?: number
  last_message_at?: string
}

export interface TicketFilters {
  status?: string
  priority?: string
  search?: string
  userId?: string
  projectId?: string
}

export const ticketsService = {
  // ============================================
  // TICKETS
  // ============================================

  /**
   * Get all tickets with filters and pagination
   */
  async getAll(
    filters?: TicketFilters,
    page = 1,
    limit = 20
  ): Promise<{ tickets: TicketWithDetails[]; total: number }> {
    let query = supabase
      .from('tickets')
      .select(`
        *,
        user:user_id(id, email, full_name, avatar_url),
        project:project_id(id, title),
        assigned_admin:assigned_to(id, full_name, email, avatar_url),
        messages(created_at)
      `, { count: 'exact' })
      .order('created_at', { ascending: false })

    // Apply filters
    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.priority) {
      query = query.eq('priority', filters.priority)
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }

    if (filters?.userId) {
      query = query.eq('user_id', filters.userId)
    }

    if (filters?.projectId) {
      query = query.eq('project_id', filters.projectId)
    }

    // Pagination
    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error

    // Add message count and last message timestamp
    const ticketsWithDetails = data?.map(ticket => ({
      ...ticket,
      message_count: ticket.messages?.length || 0,
      last_message_at: ticket.messages?.[0]?.created_at || ticket.created_at,
    })) || []

    return {
      tickets: ticketsWithDetails,
      total: count || 0,
    }
  },

  /**
   * Get a single ticket by ID with all details
   */
  async getById(id: string): Promise<TicketWithDetails | null> {
    const { data, error } = await supabase
      .from('tickets')
      .select(`
        *,
        user:user_id(id, email, full_name, avatar_url),
        project:project_id(id, title),
        assigned_admin:assigned_to(id, full_name, email, avatar_url)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Create a new ticket
   */
  async create(ticket: TicketInsert): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .insert(ticket)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update a ticket
   */
  async update(id: string, updates: TicketUpdate): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Delete a ticket
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('tickets')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Get ticket statistics
   */
  async getStats(): Promise<{
    total: number
    open: number
    in_progress: number
    closed: number
  }> {
    const { data, error } = await supabase
      .from('tickets')
      .select('status')

    if (error) throw error

    const stats = {
      total: data?.length || 0,
      open: data?.filter(t => t.status === 'open').length || 0,
      in_progress: data?.filter(t => t.status === 'in_progress').length || 0,
      closed: data?.filter(t => t.status === 'closed').length || 0,
    }

    return stats
  },

  // ============================================
  // MESSAGES
  // ============================================

  /**
   * Get all messages for a ticket
   */
  async getMessages(ticketId: string): Promise<Message[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data || []
  },

  /**
   * Send a message to a ticket
   */
  async sendMessage(message: MessageInsert): Promise<Message> {
    const { data, error } = await supabase
      .from('messages')
      .insert(message)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Subscribe to real-time messages for a ticket
   */
  subscribeToMessages(
    ticketId: string,
    callback: (message: Message) => void
  ) {
    const channel = supabase
      .channel(`ticket-${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => {
          callback(payload.new as Message)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  },

  /**
   * Subscribe to real-time ticket updates
   */
  subscribeToTicketUpdates(
    ticketId: string,
    callback: (ticket: Ticket) => void
  ) {
    const channel = supabase
      .channel(`ticket-updates-${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tickets',
          filter: `id=eq.${ticketId}`,
        },
        (payload) => {
          callback(payload.new as Ticket)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  },

  // ============================================
  // ASSIGNMENT
  // ============================================

  /**
   * Assign ticket to current admin
   */
  async assignToMe(ticketId: string, adminId: string): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({
        assigned_to: adminId,
        updated_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      })
      .eq('id', ticketId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Unassign ticket
   */
  async unassign(ticketId: string): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({
        assigned_to: null,
        updated_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      })
      .eq('id', ticketId)
      .select()
      .single()

    if (error) throw error
    return data
  },
}
